import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { useSelectedOrganization } from "@/hooks/useCurrentUser";
import { useWorkspaceStore } from "@/stores/workspaceStore";

type Brand = { id: string; name: string };

export function useCreateBrand() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useSelectedOrganization();
  const { setSelectedBrandId } = useWorkspaceStore();
  const orgId = selectedOrganization?.organization.id ?? null;

  return useMutation({
    mutationFn: async (name: string) => {
      if (!orgId) throw new Error("No organization selected");
      const res = await axiosInstance.post<string>("/brand", {
        name,
        organizationId: orgId,
      });
      return res.data;
    },
    onSuccess: (brandId, name) => {
      // Seed the new brand into the cached list immediately so the selection
      // below points at a brand that already exists. Without this, BrandPicker's
      // "default to the first brand when the selected one isn't in the list"
      // effect runs against the stale (pre-refetch) list, finds no match, and
      // clobbers the selection back to an old brand before the refetch lands.
      queryClient.setQueryData<Brand[]>(["brands", orgId], (old) =>
        old ? [...old, { id: brandId, name }] : [{ id: brandId, name }],
      );
      setSelectedBrandId(brandId);
      queryClient.invalidateQueries({ queryKey: ["brands", orgId] });
    },
  });
}
